import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bookingAPI, userAPI, companyAPI, userCompanyAPI } from '../services/api';
import Layout from '../components/Layout';
import AvailableSlotsCalendar from '../components/AvailableSlotsCalendar';
import Icon from '../components/Icon';


function BookingCalendarPage({ user, onLogout, embedded = false, darkMode = true, onToggleDarkMode }) {
  const [bikes, setBikes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [, setAvailableSlots] = useState([]);
  const [selectedTechnicianFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedBikeId, setSelectedBikeId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchBikes();
    fetchCompanies();
    fetchUserBookings();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchServiceTemplates(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId && selectedDate && selectedTemplateId) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [selectedCompanyId, selectedDate, selectedTemplateId, selectedTechnicianFilter]);

  const handleCalendarDateSelect = (dateStr) => {
    // Validate that bike, company, and service are selected before allowing date selection
    if (!selectedBikeId) {
      setError('Please select a bike first');
      return;
    }
    if (!selectedCompanyId) {
      setError('Please select a service shop first');
      return;
    }
    if (!selectedTemplateId) {
      setError('Please select a service type first');
      return;
    }
    setError(''); // Clear any previous errors
    setSelectedDate(dateStr);
    setSelectedSlot(null);
  };

  const handleCalendarSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const fetchBikes = async () => {
    try {
      const response = await userAPI.getMyBikes();
      setBikes(response.data.bikes || []);
    } catch (err) {
      setError('Failed to load bikes');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    setError('');
    try {
      const response = await userCompanyAPI.getAvailableCompanies();
      const list = response.data.companies || [];
      const connected = list.filter(
        (c) => c.connection_status === 'connected' || c.connection_status === 'accepted'
      );
      if (connected.length > 0) {
        setCompanies(connected);
      } else {
        const fallback = await companyAPI.getActiveCompanies();
        setCompanies(fallback.data.companies || []);
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      try {
        const fallback = await companyAPI.getActiveCompanies();
        setCompanies(fallback.data.companies || []);
        if (fallback.data.companies && fallback.data.companies.length === 0) {
          setError('No companies available');
        }
      } catch (err2) {
        console.error('Fallback also failed:', err2);
        const errorMsg = err2.response?.data?.error || 'Failed to load companies';
        setError(errorMsg);
        setCompanies([]);
      }
    }
  };

  const fetchUserBookings = async () => {
    try {
      setLoadingBookings(true);
      const response = await bookingAPI.getUserBookings();
      setUserBookings(response.data.bookings || []);
    } catch (err) {
      console.error('Failed to load user bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchServiceTemplates = async (companyId) => {
    if (!companyId) {
      setServiceTemplates([]);
      return;
    }
    try {
      setError('');
      const response = await bookingAPI.getCompanyServiceTemplates(companyId, true);
      setServiceTemplates(response.data.templates || []);
      if (response.data.templates && response.data.templates.length === 0) {
        setError('No service templates available for this company');
      }
    } catch (err) {
      console.error('Error loading service templates:', err);
      const errorMsg = err.response?.data?.error || 'Failed to load service templates';
      if (err.response?.status === 401) {
        setError('Please log in to view service templates');
      } else if (err.response?.status === 404) {
        setError('Company not found');
      } else {
        setError(errorMsg);
      }
      setServiceTemplates([]);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const response = await bookingAPI.getAvailableSlots(
        parseInt(selectedCompanyId),
        selectedDate,
        parseInt(selectedTemplateId),
        selectedTechnicianFilter ? parseInt(selectedTechnicianFilter) : null
      );
      setAvailableSlots(response.data.available_slots || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load available slots');
      setAvailableSlots([]);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedCompanyId || !selectedBikeId || !selectedTemplateId || !selectedDate || !selectedSlot) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Revalidate the selected slot right before creating booking to avoid stale UI/server drift.
      const latestSlotsResp = await bookingAPI.getAvailableSlots(
        parseInt(selectedCompanyId),
        selectedDate,
        parseInt(selectedTemplateId),
        selectedSlot?.technician_id || null
      );
      const latestSlots = latestSlotsResp?.data?.available_slots || [];
      const matchingSlot = latestSlots.find((slot) => {
        const sameStart = slot.start === selectedSlot.start;
        const sameEnd = !selectedSlot.end || !slot.end || slot.end === selectedSlot.end;
        const selectedTech = selectedSlot.technician_id || null;
        const slotTech = slot.technician_id || null;
        return sameStart && sameEnd && selectedTech === slotTech;
      });

      if (!matchingSlot) {
        setSelectedSlot(null);
        setError('Selected time slot is no longer available. Please choose another slot.');
        await fetchAvailableSlots();
        return;
      }

      const bookingData = {
        bike_id: parseInt(selectedBikeId),
        service_template_id: parseInt(selectedTemplateId),
        booking_date: selectedDate,
        start_time: selectedSlot.start,
        technician_id: selectedSlot.technician_id || null,
        notes: notes || null
      };

      await bookingAPI.createBooking(parseInt(selectedCompanyId), bookingData);
      setSuccess('Booking created successfully! You will receive a confirmation email.');
      setSelectedSlot(null);
      setNotes('');
      setSelectedDate('');
      setSelectedTemplateId('');
      setAvailableSlots([]);
      fetchUserBookings();
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      const apiError = err.response?.data?.error || '';
      if (apiError.toLowerCase().includes('slot') && apiError.toLowerCase().includes('available')) {
        setSelectedSlot(null);
        setError('Selected time slot is no longer available. Please choose another slot.');
        await fetchAvailableSlots();
        return;
      }
      setError(apiError || 'Failed to create booking');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const content = (
    <>
      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}
      {success && <div className="success" style={{marginBottom: '20px'}}>{success}</div>}

      <div style={{
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <form onSubmit={handleBooking}>
          {/* Company Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
              Select Company *
            </label>
            <select
              className="form-input"
              value={selectedCompanyId}
              onChange={(e) => {
                const companyId = e.target.value;
                setSelectedCompanyId(companyId);
                const company = companies.find(c => c.id === parseInt(companyId));
                setSelectedCompany(company || null);
                setServiceTemplates([]);
                setAvailableSlots([]);
                setSelectedTemplateId('');
                setSelectedSlot(null);
              }}
              required
            >
              <option value="">-- Select Company --</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
            {selectedCompany && (
              <div style={{
                marginTop: '10px',
                padding: '12px',
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.8)'
              }}>
                <strong>{selectedCompany.name}</strong>
                {selectedCompany.email && <span> | {selectedCompany.email}</span>}
              </div>
            )}
          </div>

          {/* Bike Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
              Select Bike *
            </label>
            <select className="form-input" value={selectedBikeId} onChange={(e) => setSelectedBikeId(e.target.value)} required>
              <option value="">-- Select Bike --</option>
              {bikes.map(bike => (
                <option key={bike.id} value={bike.id}>
                  {bike.brand} {bike.model} ({bike.year}) - {bike.vin}
                </option>
              ))}
            </select>
          </div>

          {/* Service Template Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
              Service Type *
            </label>
            <select
              className="form-input"
              value={selectedTemplateId}
              onChange={(e) => {
                setSelectedTemplateId(e.target.value);
                setAvailableSlots([]);
                setSelectedSlot(null);
              }}
              required
              disabled={!selectedCompanyId}
            >
              <option value="">-- Select Service Type --</option>
              {serviceTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.estimated_duration_minutes} min)
                  {template.default_price ? ` - £${Number(template.default_price).toFixed(2)}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Calendar View for Available Slots */}
          {selectedCompanyId && selectedTemplateId && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '16px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                Select Date & Time *
              </label>
              <AvailableSlotsCalendar
                companyId={selectedCompanyId}
                serviceTemplateId={selectedTemplateId}
                selectedDate={selectedDate}
                onDateSelect={handleCalendarDateSelect}
                onSlotSelect={handleCalendarSlotSelect}
              />
            </div>
          )}

          {!selectedTemplateId && (
            <div style={{
              padding: '20px',
              background: 'var(--bg-overlay-light)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '12px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.5)',
              marginBottom: '20px'
            }}>
              Please select a service type to view available dates and times
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
              Notes (Optional)
            </label>
            <textarea
              className="form-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              placeholder="Any special requests..."
              style={{ resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="submit" className="btn btn-primary" disabled={!selectedSlot}>
              Book Appointment
            </button>
            <Link to="/dashboard" className="btn btn-secondary">Cancel</Link>
          </div>
        </form>
      </div>

      {/* My Bookings Section */}
      <div style={{
        marginTop: '24px',
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#ffffff', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="calendar" color="primary" darkMode={darkMode} size={18} />
          My Bookings
        </h3>
        {loadingBookings ? (
          <div className="loading">Loading bookings...</div>
        ) : userBookings.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.5)' }}>No bookings yet.</p>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {userBookings.slice(0, 5).map(booking => {
              const statusColors = {
                completed: { bg: 'rgba(26, 40, 17, 0.15)', color: '#4A8C18' },
                cancelled: { bg: 'rgba(239, 68, 68, 0.15)', color: '#f87171' },
                confirmed: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' },
                in_progress: { bg: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa' },
                pending: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' },
              };
              const statusStyle = statusColors[booking.status] || statusColors.pending;

              return (
                <div
                  key={booking.id}
                  style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '600', color: '#ffffff' }}>
                      {new Date(booking.booking_date).toLocaleDateString()}
                    </div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>
                      {booking.start_time?.substring(0, 5)} - {booking.end_time?.substring(0, 5)}
                    </div>
                    {booking.service_template && (
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                        {booking.service_template.name}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: statusStyle.bg,
                      color: statusStyle.color,
                      textTransform: 'uppercase'
                    }}>
                      {booking.status.replace('_', ' ')}
                    </span>
                    {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                      <button
                        onClick={async () => {
                          if (window.confirm('Cancel this booking?')) {
                            try {
                              await bookingAPI.cancelBooking(booking.id);
                              setSuccess('Booking cancelled!');
                              fetchUserBookings();
                            } catch (err) {
                              setError(err.response?.data?.error || 'Failed to cancel');
                            }
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          background: 'rgba(239, 68, 68, 0.15)',
                          color: '#f87171',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout user={user} onLogout={onLogout} pageTitle="Book Service" pageSubtitle="Schedule a service appointment" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
      {content}
    </Layout>
  );
}

export default BookingCalendarPage;
